import React from 'react';
import LOGO_B64 from '../assets/tradereflex_logo_b64';
import {
  ShieldAlert, Target, Brain, BarChart2, Scale, BookOpen, Eye,
  Crosshair, Flame, Activity, AlertTriangle, CircleOff, Timer,
  FileText, TrendingDown, BadgeCheck, Zap, Star, Award,
  TrendingUp, Sparkles, ChevronRight, ArrowUp, ArrowDown,
} from 'lucide-react';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const C = {
  bg:        '#020609',   // canvas — deepest black-blue
  s1:        '#07101E',   // section surface  — clearly above canvas
  s2:        '#0D1C30',   // primary card     — another clear step up
  s3:        '#132440',   // inset/secondary  — even lighter
  b0:        '#0E1E30',   // nearly invisible spacer
  b1:        '#1C3350',   // hairline border on primary cards
  b2:        '#264666',   // highlight border / inset top
  t0:        '#F2F6FF',   // display — near white
  t1:        '#C8DDEF',   // heading — medium blue-white
  t2:        '#8AAECF',   // body — readable muted
  t3:        '#5A7A9A',   // caption — soft muted
  green:     '#22C55E',
  greenDim:  '#16A34A',
  red:       '#EF4444',
  redDim:    '#DC2626',
  blue:      '#3B82F6',
  amber:     '#F59E0B',
  purple:    '#8B5CF6',
  cyan:      '#06B6D4',
  p:  28,
  g:  16,
};

const acc  = isP => isP ? C.green : C.red;
const accD = isP => isP ? C.greenDim : C.redDim;

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt2   = v => Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN   = v => Number(v).toLocaleString('en-IN');
const fmtPct = (v, d = 2) => v != null ? `${parseFloat(v) >= 0 ? '+' : ''}${parseFloat(v).toFixed(d)}%` : null;
const fmtT   = iso => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }); }
  catch { return null; }
};
const fmtD   = iso => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }); }
  catch { return null; }
};

// ─── Design helpers ───────────────────────────────────────────────────────────

// card() returns a complete surface style object — the single source of truth for elevation
const card = (extra = {}) => ({
  background: `linear-gradient(165deg, ${C.s2} 0%, ${C.s1} 100%)`,
  borderRadius: 14,
  border: `1px solid ${C.b1}`,
  borderTop: `1px solid ${C.b2}`,
  boxShadow: `0 2px 0 ${C.b0}, 0 8px 40px rgba(0,0,0,0.55), inset 0 1px 0 ${C.b2}60`,
  ...extra,
});

const inset = (extra = {}) => ({
  background: `linear-gradient(160deg, ${C.s3} 0%, ${C.s2}CC 100%)`,
  borderRadius: 9,
  borderLeft: `2px solid transparent`,
  boxShadow: `inset 0 1px 0 ${C.b2}30, 0 1px 8px rgba(0,0,0,0.25)`,
  ...extra,
});

// ─── Atoms ────────────────────────────────────────────────────────────────────

const HR = ({ style }) => (
  <div style={{ height: 1, background: C.b1, flexShrink: 0, ...style }} />
);

const VR = ({ h = 20 }) => (
  <div style={{ width: 1, height: h, background: C.b1, flexShrink: 0 }} />
);

const Chip = ({ label, color }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 10px', borderRadius: 99,
    background: `${color}18`, border: `1px solid ${color}40`,
    color, fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
  }}>{label}</span>
);

// Caption label — always subdued, uppercase
const Label = ({ children, style }) => (
  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: C.t2, ...style }}>
    {children}
  </div>
);

// Metric number — large, dominant
const Metric = ({ value, color, size = 24 }) => (
  <div style={{ color: value ? color || C.t0 : C.t3, fontSize: size, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1, fontStyle: value ? 'normal' : 'italic' }}>
    {value || '—'}
  </div>
);

function DonutRing({ score, max = 100, size = 72, color, strokeWidth = 6 }) {
  const r    = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const pct  = Math.min(1, Math.max(0, score / max));
  const col  = color || (pct >= 0.6 ? C.green : pct >= 0.4 ? C.amber : C.red);
  const disp = max === 10 ? (typeof score === 'number' ? score.toFixed(1) : score) : Math.round(score);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" strokeWidth={strokeWidth} stroke={C.b1} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" strokeWidth={strokeWidth}
          stroke={col} strokeDasharray={`${pct * circ} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: col, fontSize: size > 60 ? 16 : 12, fontWeight: 900, lineHeight: 1, letterSpacing: -0.5 }}>{disp}</span>
        <span style={{ color: C.t3, fontSize: 8, marginTop: 1 }}>/{max}</span>
      </div>
    </div>
  );
}

function BarTrack({ value, max = 100, color, h = 3 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const col = color || (pct >= 60 ? C.green : pct >= 40 ? C.amber : C.red);
  return (
    <div style={{ height: h, background: C.b0, borderRadius: 99, overflow: 'hidden', position: 'relative' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: col, borderRadius: 99, opacity: 0.9 }} />
    </div>
  );
}

function SectionHead({ n, title, color }) {
  const col = color || C.blue;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      {/* Filled number chip — clearly visible */}
      <div style={{
        width: 26, height: 26, borderRadius: 7, flexShrink: 0,
        background: col, display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 0 12px ${col}50`,
      }}>
        <span style={{ color: '#fff', fontSize: 11, fontWeight: 900, lineHeight: 1 }}>{String(n).padStart(2,'0')}</span>
      </div>
      <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: C.t0 }}>
        {title}
      </span>
      <div style={{ flex:1, height:1, background: `linear-gradient(90deg, ${col}40, transparent)` }} />
    </div>
  );
}

// ─── Data Pools ───────────────────────────────────────────────────────────────
const SEV_COLOR = { HIGH: C.red, MED: C.amber, LOW: C.t3, WIN: C.green };

const LOSS_POOL = [
  { icon: Crosshair,    title: 'No Clear Plan',        sev: 'HIGH', desc: d => !d.sl && !d.target ? 'Entered without predefined rules, criteria, or exit target.' : null },
  { icon: ShieldAlert,  title: 'No Stop Loss',         sev: 'HIGH', desc: d => !d.sl ? 'Entered without defining downside. Unlimited risk exposure.' : null },
  { icon: Target,       title: 'No Target Set',        sev: 'HIGH', desc: d => !d.target ? 'No clear exit plan. Risk:Reward completely undefined.' : null },
  { icon: Scale,        title: 'Poor Risk:Reward',     sev: 'HIGH', desc: d => (d.rr != null && d.rr < 1) ? `${d.rr.toFixed(1)}:1 — risked more than potential reward.` : null },
  { icon: Flame,        title: 'Emotional Entry',      sev: 'HIGH', desc: d => (d.conf != null && d.conf < 5) ? `Only ${d.conf}/10 confidence — entered despite the doubt.` : null },
  { icon: BarChart2,    title: 'Against the Regime',   sev: 'MED',  desc: d => d.mismatch ? 'Trade direction conflicted with the day market bias.' : null },
  { icon: Eye,          title: 'Trade Not Tagged',     sev: 'LOW',  desc: d => !d.tagged ? 'No setup tagged. Cannot learn from patterns never tracked.' : null },
  { icon: BookOpen,     title: 'Not Journaled',        sev: 'LOW',  desc: d => !d.journaled ? 'Unlogged trades break the review loop needed to grow.' : null },
  { icon: Timer,        title: 'Quick SL Hit',         sev: 'MED',  desc: d => (d.outcome === 'sl_hit' && d.days != null && d.days <= 1) ? 'Stop hit same session — setup or timing was wrong.' : null },
  { icon: Activity,     title: 'Poor Market Timing',   sev: 'MED',  desc: () => 'Market conditions were not aligned with the trade direction.' },
  { icon: AlertTriangle,title: 'Bad Risk Management',  sev: 'HIGH', desc: () => 'Risk per trade too high relative to your account size.' },
  { icon: CircleOff,    title: 'Exited Without Plan',  sev: 'MED',  desc: () => 'No target defined. Exited in loss without following any rule.' },
  { icon: TrendingDown, title: 'Low Reward Potential', sev: 'MED',  desc: () => 'No clear upside target. Risk:Reward was heavily one-sided.' },
  { icon: FileText,     title: 'No Review Habit',      sev: 'LOW',  desc: () => 'Lack of consistency reviewing and improving past trades.' },
];

const WIN_POOL = [
  { icon: ShieldAlert, title: 'Stop Loss Placed',    sev: 'WIN', desc: d => d.sl      ? 'Risk defined before entering. Pure discipline.' : null },
  { icon: Target,      title: 'Target Defined',      sev: 'WIN', desc: d => d.target  ? 'Clear exit plan. No guessing on the way out.' : null },
  { icon: Scale,       title: 'Good Risk:Reward',    sev: 'WIN', desc: d => (d.rr != null && d.rr >= 1.5) ? `${d.rr.toFixed(1)}:1 — reward well justified the risk.` : null },
  { icon: Eye,         title: 'Trade Tagged',        sev: 'WIN', desc: d => d.tagged   ? 'Tagging keeps you accountable and builds pattern memory.' : null },
  { icon: BookOpen,    title: 'Journaled',           sev: 'WIN', desc: d => d.journaled? 'You logged the trade. Review loops compound skill.' : null },
  { icon: Brain,       title: 'High Confidence',     sev: 'WIN', desc: d => (d.conf != null && d.conf >= 7) ? `Entered with ${d.conf}/10 confidence — trusted your read.` : null },
  { icon: BarChart2,   title: 'Followed the Regime', sev: 'WIN', desc: d => d.match   ? 'Trade direction matched the day regime. Process over gut.' : null },
  { icon: Crosshair,   title: 'Target Hit',          sev: 'WIN', desc: d => d.outcome === 'target_hit' ? 'Let the trade run to full potential.' : null },
  { icon: BadgeCheck,  title: 'Consistent Process',  sev: 'WIN', desc: () => 'Repeating disciplined entries is how an edge compounds.' },
  { icon: Zap,         title: 'Planned Entry',       sev: 'WIN', desc: () => 'Clear thesis before entering. Preparation paid off.' },
  { icon: Star,        title: 'Right Market Day',    sev: 'WIN', desc: () => 'Picked a trade on a day the market cooperated with the setup.' },
  { icon: Award,       title: 'Held Your Nerve',     sev: 'WIN', desc: () => 'Stayed in the trade without panic-exiting early.' },
];

function buildCards(pool, d, n = 6) {
  const out = [];
  for (const it of pool) {
    if (out.length >= n) break;
    const desc = it.desc(d);
    if (desc) out.push({ ...it, desc });
  }
  const gen = pool.filter(i => typeof i.desc({}) === 'string');
  let gi = 0;
  while (out.length < n && gi < gen.length) {
    if (!out.find(c => c.title === gen[gi].title))
      out.push({ ...gen[gi], desc: gen[gi].desc({}) });
    gi++;
  }
  return out.slice(0, n);
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function TradeShareCard_v2({ trade, snap, cardRef }) {
  const res   = snap?.actual_results  || {};
  const setup = snap?.trade_setup     || {};
  const mkt   = snap?.market_context  || {};
  const disc  = snap?.discipline_snap || {};
  const chk   = snap?.checklist       || {};
  const de    = chk.decision_engine   || {};
  const foC   = snap?.fo_contract     || null;
  const rg    = mkt.regime            || {};
  const aiRaw = snap?.ai_review       || null;

  const pnl    = parseFloat(res.pnl ?? trade?.pnl ?? 0);
  const pnlPct = parseFloat(res.pnl_pct ?? trade?.pnlPct ?? 0);
  const isP    = pnl >= 0;
  const A      = acc(isP);
  const AD     = accD(isP);

  const sym    = (setup.symbol || trade?.symbol || '').replace('.NS','');
  const co     = setup.company || trade?.company || null;
  const entPx  = parseFloat(setup.entry_price  ?? trade?.buyPrice  ?? 0);
  const exPx   = parseFloat(res.exit_price     ?? trade?.sellPrice ?? 0);
  const sl     = setup.stop_loss    ? parseFloat(setup.stop_loss)    : null;
  const tgt    = setup.target_price ? parseFloat(setup.target_price) : null;
  const qty    = setup.quantity     || trade?.quantity  || null;
  const days   = res.holding_days   ?? trade?.holdingDays ?? null;
  const oc     = res.outcome;
  const rr     = disc.rr_ratio ? parseFloat(disc.rr_ratio) : null;
  const tags   = setup.strategy_tags || trade?.strategyTags || [];

  const pt     = (snap?.product_type || trade?.productType || trade?.tradeVariant || '').toUpperCase();
  const intra  = pt === 'INTRADAY' || pt === 'MIS' || days === 0 || days === 1;
  const ttl    = intra ? 'INTRADAY' : (days != null && days <= 5) ? 'SWING' : days != null ? 'POSITIONAL' : null;

  const sim    = snap?.sim_date || trade?.simDate || null;
  const entIso = setup.entry_date || trade?.entryDate || null;
  const exIso  = res.exit_date   || trade?.exitDate   || null;
  const entT   = fmtT(entIso);
  const exT    = fmtT(exIso);
  const entD   = fmtD(entIso);
  const exD    = fmtD(exIso);

  const pnlStr    = (isP ? '+' : '−') + '₹' + fmtN(Math.abs(Math.round(pnl)));
  const pnlPctStr = (isP ? '+' : '') + pnlPct.toFixed(2) + '%';

  const ocLabel = oc === 'sl_hit' ? 'SL HIT' : oc === 'target_hit' ? 'TARGET HIT' : oc === 'eod' ? 'EOD EXIT' : oc === 'manual' ? 'MANUAL EXIT' : null;
  const ocColor = oc === 'sl_hit' ? C.red : oc === 'target_hit' ? C.green : C.t2;

  const match   = rg.match === true;
  const mismatch= rg.match === false;
  const conf    = chk.confidence;
  const confC   = conf >= 7 ? C.green : conf >= 5 ? C.amber : C.red;

  const d = { sl, target: tgt, rr, tagged: disc.tagged, journaled: disc.journaled, conf, match, mismatch, outcome: oc, days };
  const cards = buildCards(isP ? WIN_POOL : LOSS_POOL, d, 6);

  const dScore = disc.score ?? null;
  const plan   = conf != null ? conf * 10 : 0;
  const exec   = disc.exit_quality != null ? disc.exit_quality * 10 : (oc === 'target_hit' ? 80 : oc === 'sl_hit' ? 30 : 0);
  const risk   = rr != null ? Math.min(100, Math.round(rr * 33)) : (sl && tgt ? 60 : 20);
  const mind   = conf != null ? Math.round((conf / 10) * (match ? 90 : mismatch ? 40 : 65)) : 0;

  const qs     = disc.trade_quality_score ?? (dScore != null ? parseFloat((dScore / 10).toFixed(1)) : 0);
  const qC     = qs >= 7 ? C.green : qs >= 5 ? C.amber : C.red;
  const qLbl   = qs >= 7 ? 'HIGH QUALITY' : qs >= 5 ? 'AVERAGE' : 'BELOW AVERAGE';

  const aiLines     = aiRaw ? aiRaw.split('\n').filter(l => l.trim()).slice(0, 3) : [];

  const globalIdx   = mkt.global_indices || [];
  const prevDay     = globalIdx.filter(i => ['US','Europe'].includes(i.region));
  const fii         = mkt.fii_dii        || {};
  const sentiment   = mkt.market_sentiment || null;
  const gapType     = rg.gap_type   || null;
  const gapValue    = rg.gap_value  != null ? parseFloat(rg.gap_value) : null;
  const gapLabel    = rg.gap_label  || (gapType ? gapType.replace(/_/g,' ') : null);
  const gapColor    = { strong_up: C.green, mild_up: C.cyan, strong_down: C.red, mild_down: '#f97316', flat: C.t2 }[gapType] || C.t2;
  const bullSig     = rg.bull_signals != null ? rg.bull_signals : null;
  const bearSig     = rg.bear_signals != null ? rg.bear_signals : null;
  const sectorLead  = rg.sector_leader  || null;
  const sectorLag   = rg.sector_laggard || null;

  const SS = ['PULLBACK','FOLLOW_TREND','BREAKOUT','REVERSAL','MOMENTUM','RANGE_BOUND','GAP_FILL','TREND_CONTINUATION'];
  const sw = tags.find(t => SS.includes(t?.toUpperCase()?.replace(/ /g,'_')));

  const vLbl = isP ? (qs >= 7 ? 'HIGH QUALITY TRADE' : 'AVERAGE TRADE') : (qs < 4 ? 'LOW QUALITY TRADE' : 'BELOW AVERAGE');

  const actualR    = trade?.actualR != null ? parseFloat(trade.actualR) : null;
  const tradeReason= chk.trade_reason || snap?.user_decision?.reason || trade?.notes || null;

  const exitQuality = (() => {
    if (!tgt || !exPx) return null;
    const tol = tgt * 0.02;
    const isShortOption = foC && ['SHORT_CE','SHORT_PE','SELL_CE','SELL_PE'].includes((foC.contract_type||'').toUpperCase());
    if (Math.abs(exPx - tgt) <= tol)                              return { label: 'Clean Exit',      color: C.green };
    if (!isShortOption && exPx < tgt - tol && isP)                return { label: 'Early Exit',       color: C.amber };
    if (!isShortOption && exPx > tgt + tol && isP)                return { label: 'Held Past Target', color: C.purple };
    if (!isShortOption && exPx > tgt + tol && !isP)               return { label: 'Greed Exit',       color: C.red };
    return null;
  })();

  const W = 1080, H = 1350;
  const P = C.p, G = C.g;

  return (
    <div ref={cardRef} style={{
      width: W, height: H, overflow: 'hidden', boxSizing: 'border-box',
      background: C.bg,
      fontFamily: "'Inter','SF Pro Display','Segoe UI',system-ui,sans-serif",
      position: 'relative',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ══ Canvas background — layered depth ══ */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:0 }}>
        {/* Top-right accent glow — accent colour, faint */}
        <div style={{ position:'absolute', top:-160, right:-100, width:600, height:600, borderRadius:'50%',
          background:`radial-gradient(circle, ${A}0E 0%, transparent 60%)` }} />
        {/* Bottom-left cool glow */}
        <div style={{ position:'absolute', bottom:-80, left:-80, width:500, height:500, borderRadius:'50%',
          background:`radial-gradient(circle, ${C.blue}08 0%, transparent 60%)` }} />
        {/* Vignette — darkens corners */}
        <div style={{ position:'absolute', inset:0,
          background:'radial-gradient(ellipse 100% 100% at 50% 50%, transparent 40%, rgba(0,0,0,0.45) 100%)' }} />
      </div>

      {/* Top accent bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, zIndex:2,
        background:`linear-gradient(90deg, transparent 3%, ${A}60 25%, ${A} 50%, ${A}60 75%, transparent 97%)` }} />

      {/* ══════════════════════════════════════════════════════════════════
          HERO — distinct surface, cinematic feel
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        flexShrink: 0, zIndex: 1, position: 'relative', overflow: 'hidden',
        padding: `18px ${P}px 18px`,
        /* Hero surface: noticeably lighter than canvas */
        background: `linear-gradient(150deg, ${C.s2}F8 0%, ${C.s2}DD 50%, ${C.s1}EE 100%)`,
        borderBottom: `1px solid ${C.b2}`,
        boxShadow: `0 1px 0 ${C.b0}, 0 16px 64px rgba(0,0,0,0.70)`,
      }}>
        {/* Hero interior lighting */}
        <div style={{ position:'absolute', inset:0, pointerEvents:'none',
          background:`radial-gradient(ellipse 60% 120% at 105% 45%, ${A}10 0%, transparent 55%)` }} />
        <div style={{ position:'absolute', top:-80, left:-60, width:360, height:360, borderRadius:'50%', pointerEvents:'none',
          background:`radial-gradient(circle, ${C.blue}08 0%, transparent 65%)` }} />
        {/* Top inset highlight */}
        <div style={{ position:'absolute', top:0, left:0, right:0, height:1, pointerEvents:'none',
          background:`linear-gradient(90deg, transparent 5%, ${C.b2}CC 40%, ${C.b2}CC 60%, transparent 95%)` }} />

        {/* ── Top bar: logo left, report label + date right ── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, position:'relative', zIndex:1 }}>
          <img src={LOGO_B64} alt="TradeReflex" style={{ height:30, width:'auto', objectFit:'contain', flexShrink:0 }} />
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ color:C.t2, fontSize:10, fontWeight:700, letterSpacing:1.8, textTransform:'uppercase' }}>Trade Analysis Report</span>
            {(sim || exD) && (
              <div style={{ background:`${C.b2}50`, border:`1px solid ${C.b2}`, borderRadius:7, padding:'3px 10px' }}>
                <span style={{ color:C.t1, fontSize:10, fontWeight:700, letterSpacing:0.5 }}>{sim ? `SIM · ${sim}` : exD}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Hero body ── */}
        <div style={{ display:'flex', gap:0, position:'relative', zIndex:1 }}>

          {/* LEFT — identity + headline */}
          <div style={{ flex:1, minWidth:0, paddingRight:P+4,
            borderRight:`1px solid ${C.b1}`,
            display:'flex', flexDirection:'column', justifyContent:'space-between', gap:10 }}>

            {/* Status pill */}
            <div style={{ display:'inline-flex', alignSelf:'flex-start', alignItems:'center', gap:7,
              background:`${A}12`, border:`1px solid ${A}35`, borderRadius:99, padding:'4px 14px' }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:A, boxShadow:`0 0 8px ${A}` }} />
              <span style={{ color:A, fontSize:11, fontWeight:800, letterSpacing:1, textTransform:'uppercase' }}>
                {isP ? 'Profitable Trade' : 'Losing Trade'}
              </span>
            </div>

            {/* Cinematic headline — single line */}
            <div style={{ letterSpacing:-1.5 }}>
              <span style={{ color:C.t0, fontSize:40, fontWeight:900, lineHeight:1 }}>COMPLETE TRADE </span>
              <span style={{ color:A,    fontSize:40, fontWeight:900, lineHeight:1 }}>{isP ? 'SUCCESS' : 'ANALYSIS'}</span>
            </div>

            {/* Dates */}
            <div style={{ display:'flex', gap:18 }}>
              {sim && (
                <div>
                  <Label style={{ marginBottom:3 }}>Sim Date</Label>
                  <div style={{ color:C.t1, fontSize:12, fontWeight:700 }}>{sim}</div>
                </div>
              )}
              {entD && (
                <div>
                  <Label style={{ marginBottom:3 }}>Entry</Label>
                  <div style={{ color:C.t1, fontSize:12, fontWeight:700 }}>{entD}{entT ? ` · ${entT}` : ''}</div>
                </div>
              )}
              {exD && (
                <div>
                  <Label style={{ marginBottom:3 }}>Exit</Label>
                  <div style={{ color:C.t1, fontSize:12, fontWeight:700 }}>{exD}{exT ? ` · ${exT}` : ''}</div>
                </div>
              )}
            </div>

            {/* Chips */}
            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
              {ttl        && <Chip label={ttl}                  color={A}       />}
              {pt         && <Chip label={pt}                   color={C.cyan}  />}
              {de?.grade  && <Chip label={`GRADE ${de.grade}`}  color={C.blue}  />}
              {sw         && <Chip label={sw.replace(/_/g,' ')} color={C.purple}/>}
              {ocLabel    && <Chip label={ocLabel}              color={ocColor} />}
            </div>
          </div>

          {/* RIGHT — verdict + P&L + score together */}
          <div style={{ flex:1, minWidth:0, paddingLeft:P+4,
            display:'flex', flexDirection:'column', justifyContent:'space-between', gap:10 }}>

            {/* Verdict label */}
            <div>
              <Label style={{ marginBottom:4 }}>Overall Verdict</Label>
              <div style={{ color:A, fontSize:18, fontWeight:900, letterSpacing:-0.4, lineHeight:1 }}>{vLbl}</div>
              <div style={{ color:C.t1, fontSize:11, fontWeight:600, lineHeight:1.5, marginTop:5, maxWidth:220 }}>
                {chk?.verdict || (isP ? 'Disciplined execution with a clear process.' : 'Weak setup, poor execution, emotional decisions.')}
              </div>
            </div>

            {/* P&L + Score card side by side */}
            <div style={{ display:'flex', alignItems:'flex-end', gap:18 }}>
              <div style={{ flex:1 }}>
                <Label style={{ marginBottom:5 }}>Net P&amp;L</Label>
                <div style={{ color:A, fontSize:52, fontWeight:900, letterSpacing:-3, lineHeight:0.88 }}>
                  {pnlStr}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:7, flexWrap:'wrap' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:3 }}>
                    {isP ? <ArrowUp size={11} style={{ color:A }} /> : <ArrowDown size={11} style={{ color:A }} />}
                    <span style={{ color:AD, fontSize:14, fontWeight:800 }}>{pnlPctStr}</span>
                  </div>
                  {ocLabel && <Chip label={ocLabel} color={ocColor} />}
                  {exitQuality && <Chip label={exitQuality.label} color={exitQuality.color} />}
                  {actualR != null && (
                    <div style={{ display:'flex', alignItems:'baseline', gap:2 }}>
                      <span style={{ color:C.t3, fontSize:9, fontWeight:700, letterSpacing:0.8 }}>ACTUAL R</span>
                      <span style={{ color: actualR >= 0 ? C.green : C.red, fontSize:13, fontWeight:900 }}>
                        {actualR >= 0 ? '+' : ''}{actualR.toFixed(2)}R
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {/* Score donut right beside P&L */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, flexShrink:0 }}>
                <DonutRing score={qs} max={10} size={68} color={A} strokeWidth={6} />
                <span style={{ color:qC, fontSize:9, fontWeight:800, letterSpacing:1, textTransform:'uppercase' }}>{qLbl}</span>
              </div>
            </div>

            {/* Confidence */}
            {conf != null && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <Label>Entry Confidence</Label>
                  <span style={{ color:confC, fontSize:11, fontWeight:800 }}>{conf}/10</span>
                </div>
                <BarTrack value={conf} max={10} color={confC} h={4} />
              </div>
            )}

            {/* SL / TGT / R:R */}
            <div style={{ display:'flex', alignItems:'center', gap:0 }}>
              {[
                { label: sl  ? `SL ₹${fmt2(sl)}`   : 'No SL',     color: sl  ? C.green : C.t3 },
                { label: tgt ? `TGT ₹${fmt2(tgt)}`  : 'No Target', color: tgt ? C.green : C.t3 },
                ...(rr != null ? [{ label: `${rr.toFixed(1)}:1 R:R`, color: rr >= 1.5 ? C.green : C.amber }] : []),
              ].map((m, i, a) => (
                <React.Fragment key={i}>
                  <span style={{ color:m.color, fontSize:11, fontWeight:700 }}>{m.label}</span>
                  {i < a.length-1 && <span style={{ color:C.b2, fontSize:13, margin:'0 10px' }}>·</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hero → content fade */}
      <div style={{ height:1, background:`linear-gradient(90deg, transparent, ${C.b2}80 20%, ${C.b2}80 80%, transparent)`, flexShrink:0, zIndex:1 }} />

      {/* ── Content area ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8, padding:`10px ${P}px 10px`, overflow:'hidden', position:'relative', zIndex:1 }}>

        {/* ══ 01  TRADE SNAPSHOT ══ */}
        <div style={{ flexShrink:0 }}>
          <SectionHead n={1} title="Trade Snapshot" color={C.blue} />
          <div style={card({ padding:'11px 16px' })}>
            {/* Instrument row */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ color:C.t0, fontSize:26, fontWeight:900, letterSpacing:-0.5 }}>{sym || 'INSTRUMENT'}</span>
                  {foC?.contract_type && <Chip label={foC.contract_type} color={C.blue} />}
                  {pt && <Chip label={pt} color={C.t3} />}
                </div>
                {co && <div style={{ color:C.t1, fontSize:11, fontWeight:600 }}>{co}</div>}
                {foC && (
                  <div style={{ display:'flex', gap:5, marginTop:5, flexWrap:'wrap' }}>
                    {foC.strike     && <Chip label={`₹${foC.strike.toLocaleString('en-IN')}`} color={C.amber} />}
                    {foC.moneyness  && <Chip label={foC.moneyness}                             color={C.cyan}  />}
                    {foC.expiry     && <Chip label={foC.expiry}                                color={C.t2}    />}
                    {foC.days_to_expiry != null && <Chip label={`${foC.days_to_expiry}d to expiry`} color={foC.days_to_expiry <= 3 ? C.red : C.t2} />}
                  </div>
                )}
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ color:A, fontSize:26, fontWeight:900, letterSpacing:-1, lineHeight:1 }}>{pnlStr}</div>
                <div style={{ display:'flex', gap:5, justifyContent:'flex-end', marginTop:5 }}>
                  <Chip label={pnlPctStr} color={A} />
                  {ocLabel && <Chip label={ocLabel} color={ocColor} />}
                </div>
              </div>
            </div>

            <HR style={{ marginBottom:11 }} />

            {/* 4 metrics */}
            <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:11 }}>
              {[
                { label:'Entry Price', value: entPx > 0 ? `₹${fmt2(entPx)}` : null, color:C.blue },
                { label:'Exit Price',  value: exPx  > 0 ? `₹${fmt2(exPx)}`  : null, color:A },
                { label:'Quantity',    value: qty ? String(qty) : null,               color:C.t1 },
                {
                  label: intra ? 'Hold Time' : 'Hold Days',
                  value: intra
                    ? (() => { try { const d2=Math.round((new Date(exIso)-new Date(entIso))/60000); return d2>0?`${d2}m`:(days!=null?`${Math.round(days*1440)}m`:null); } catch{return null;} })()
                    : (days != null ? `${days}d` : null),
                  color:C.t1,
                },
              ].map((m, i, a) => (
                <React.Fragment key={i}>
                  <div style={{ flex:1, paddingRight:i<a.length-1?16:0, paddingLeft:i>0?16:0 }}>
                    <Label style={{ marginBottom:4 }}>{m.label}</Label>
                    <Metric value={m.value} color={m.color} size={22} />
                  </div>
                  {i < a.length-1 && <VR h={30} />}
                </React.Fragment>
              ))}
            </div>

            <HR style={{ marginBottom:11 }} />

            {/* Context row */}
            <div style={{ display:'flex', gap:26 }}>
              {[
                { label:'Strategy',   value: sw ? sw.replace(/_/g,' ') : tags?.[0], color:C.purple },
                { label:'Market Day', value: rg?.day_type,                           color:C.t1 },
                { label:'Day Bias',   value: rg?.global_bias?.toUpperCase(),         color: rg?.global_bias==='bull'?C.green:rg?.global_bias==='bear'?C.red:C.t1 },
                { label:'Confidence', value: conf!=null?`${conf}/10`:null,           color:confC },
                { label:'R:R Ratio',  value: rr!=null?`${rr.toFixed(1)}:1`:null,    color: rr>=1.5?C.green:C.amber },
              ].map((m, i) => (
                <div key={i}>
                  <Label style={{ marginBottom:4 }}>{m.label}</Label>
                  <div style={{ color:m.value?(m.color||C.t0):C.t3, fontSize:14, fontWeight:900, fontStyle:m.value?'normal':'italic' }}>
                    {m.value||'—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ 02 ANALYSIS + 03/04 MISTAKES ══ */}
        <div style={{ display:'flex', gap:G, flexShrink:0, width:'100%' }}>

          {/* Analysis — 42% */}
          <div style={{ flex:'0 0 42%', minWidth:0, display:'flex', flexDirection:'column' }}>
            <SectionHead n={2} title="Analysis" color={C.cyan} />
            <div style={card({ padding:'10px 12px', flex:1 })}>

              {/* Ring + bars */}
              <div style={{ display:'flex', gap:12, marginBottom:9 }}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, flexShrink:0 }}>
                  <DonutRing score={dScore ?? 0} max={100} size={58} strokeWidth={5} />
                  <Label>Discipline</Label>
                </div>
                <div style={{ flex:1, display:'flex', flexDirection:'column', gap:7, justifyContent:'center' }}>
                  {[
                    { l:'Planning',  v: plan },
                    { l:'Execution', v: exec },
                    { l:'Risk Mgmt', v: risk },
                    { l:'Mindset',   v: mind },
                  ].map(m => (
                    <div key={m.l}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                        <span style={{ color:C.t1, fontSize:12, fontWeight:700 }}>{m.l}</span>
                        <span style={{ color:m.v>=60?C.green:m.v>=40?C.amber:C.red, fontSize:12, fontWeight:900 }}>{m.v}</span>
                      </div>
                      <BarTrack value={m.v} h={3} />
                    </div>
                  ))}
                </div>
              </div>

              <HR style={{ marginBottom:8 }} />

              {/* Trade quality inset */}
              <div style={inset({
                borderLeft:`2px solid ${qC}70`,
                padding:'8px 12px', marginBottom:8,
                display:'flex', alignItems:'center', justifyContent:'space-between',
              })}>
                <div>
                  <Label style={{ marginBottom:3 }}>Trade Quality</Label>
                  <div style={{ color:qC, fontSize:12, fontWeight:900, letterSpacing:1, textTransform:'uppercase', marginTop:2 }}>{qLbl}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <span style={{ color:qC, fontSize:28, fontWeight:900, letterSpacing:-1, lineHeight:1 }}>{qs}</span>
                  <span style={{ color:C.t3, fontSize:10, marginLeft:2 }}>/10</span>
                </div>
              </div>
              <BarTrack value={qs} max={10} color={qC} h={3} />

              <HR style={{ margin:'8px 0' }} />

              {/* Market context */}
              <div style={{ marginBottom:8 }}>
                <Label style={{ marginBottom:5 }}>Market Context</Label>
                <div style={{ display:'flex', gap:18, marginBottom:rg?.verdict_sentence ? 7 : 0 }}>
                  {[
                    { l:'Trend', v:rg?.global_bias?.toUpperCase(), c:rg?.global_bias==='bull'?C.green:rg?.global_bias==='bear'?C.red:C.t1 },
                    { l:'NIFTY', v:rg?.nifty_change_pct!=null?fmtPct(rg.nifty_change_pct):null, c:(rg?.nifty_change_pct??0)>=0?C.green:C.red },
                    { l:'VIX',   v:mkt?.vix!=null?String(mkt.vix):null, c:C.t1 },
                  ].map(m => (
                    <div key={m.l}>
                      <Label style={{ marginBottom:3 }}>{m.l}</Label>
                      <div style={{ color:m.v?(m.c||C.t0):C.t3, fontSize:15, fontWeight:900, fontStyle:m.v?'normal':'italic' }}>{m.v||'—'}</div>
                    </div>
                  ))}
                </div>
                {rg?.verdict_sentence && (
                  <div style={{ color:C.t1, fontSize:11, fontWeight:600, lineHeight:1.6, fontStyle:'italic', paddingTop:7, borderTop:`1px solid ${C.b1}` }}>
                    {rg.verdict_sentence}
                  </div>
                )}
              </div>

              <HR style={{ marginBottom:10 }} />

              {/* R:R + Outcome */}
              <div style={{ display:'flex', gap:0 }}>
                <div style={{ flex:1, paddingRight:14 }}>
                  <Label style={{ marginBottom:5 }}>Risk / Reward</Label>
                  <Metric value={rr!=null?`${rr.toFixed(1)}:1`:null} color={rr!=null?(rr>=1.5?C.green:C.amber):C.t3} size={23} />
                  <div style={{ color:C.t2, fontSize:10, fontWeight:700, marginTop:4 }}>{!rr?'Undefined':rr>=1.5?'Good ratio':'Acceptable'}</div>
                </div>
                <VR h={50} />
                <div style={{ flex:1, paddingLeft:14 }}>
                  <Label style={{ marginBottom:5 }}>Outcome</Label>
                  <Metric value={pnlStr} color={A} size={23} />
                  <div style={{ color:AD, fontSize:11, fontWeight:800, marginTop:4 }}>{pnlPctStr}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Mistakes/Wins — 58% */}
          <div style={{ flex:'1 1 0', minWidth:0, display:'flex', flexDirection:'column' }}>
            <SectionHead n={isP?3:4} title={isP?'What You Nailed':'Mistakes Identified'} color={A} />
            <div style={{ display:'flex', flexDirection:'column', gap:5, width:'100%' }}>
              {cards.map((card_item, i) => {
                const sCol  = SEV_COLOR[isP ? 'WIN' : card_item.sev] || C.amber;
                const Icon  = card_item.icon;
                const isTop = i < 2;
                const isMid = i === 2 || i === 3;
                return (
                  <div key={i} style={{
                    display:'flex', alignItems:'center', gap:11, width:'100%',
                    background: isTop
                      ? `linear-gradient(135deg, ${C.s2} 0%, ${C.s2}CC 100%)`
                      : isMid ? `${C.s1}CC` : 'transparent',
                    borderRadius: 10,
                    padding: isTop ? '10px 13px' : '8px 13px',
                    border: `1px solid ${isTop ? C.b1 : isMid ? C.b0 : 'transparent'}`,
                    borderLeft: `3px solid ${sCol}${isTop ? '' : isMid ? 'AA' : '55'}`,
                    boxShadow: isTop ? `0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 ${C.b2}50` : 'none',
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                      <span style={{ color:C.t3, fontSize:10, fontWeight:700, width:12, textAlign:'right' }}>{i+1}</span>
                      <div style={{ width:32, height:32, borderRadius:8, flexShrink:0,
                        background:`${sCol}${isTop ? '20' : '12'}`,
                        border:`1px solid ${sCol}${isTop ? '40' : '25'}`,
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {Icon && <Icon size={14} style={{ color:sCol, opacity: isTop ? 1 : 0.8 }} />}
                      </div>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
                        <span style={{ color:isTop?C.t0:isMid?C.t1:C.t2, fontSize:15, fontWeight:800 }}>{card_item.title}</span>
                        <span style={{ color:sCol, fontSize:9, fontWeight:700, letterSpacing:0.8,
                          background:`${sCol}15`, borderRadius:4, padding:'2px 7px', textTransform:'uppercase' }}>
                          {isP ? 'WIN' : card_item.sev}
                        </span>
                      </div>
                      <div style={{ color:isTop?C.t1:C.t2, fontSize:12, fontWeight:600, lineHeight:1.5,
                        overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                        {card_item.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ══ AI REVIEW ══ */}
        <div style={{ flexShrink:0 }}>
          <SectionHead n={isP?4:5} title="AI Review" color={C.purple} />
          <div style={card({ padding:'10px 14px' })}>
            <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
              {aiLines.length > 0 ? (
                <div style={{ flex:1, display:'flex', flexDirection:'column', gap:9 }}>
                  {aiLines.map((line, i) => (
                    <div key={i} style={{ display:'flex', gap:9, alignItems:'flex-start' }}>
                      <ChevronRight size={10} style={{ color:C.purple, marginTop:3, flexShrink:0 }} />
                      <span style={{ color:C.t0, fontSize:13, lineHeight:1.6, fontWeight:600 }}>{line}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ flex:1, display:'flex', gap:14 }}>
                  {[
                    { label:'Strengths', color:C.green,
                      text:[disc.tagged&&'Setup tagged', disc.sl_placed&&'SL placed', disc.journaled&&'Journaled'].filter(Boolean).join(' · ')||'No notable strengths captured.' },
                    { label:'Weaknesses', color:C.red,
                      text:[!sl&&'No SL defined', !tgt&&'No target set', mismatch&&'Against regime'].filter(Boolean).join(' · ')||'No critical weaknesses flagged.' },
                    { label:'Behavioral', color:C.amber,
                      text:(conf!=null&&conf<5?`Low confidence (${conf}/10). `:'')+
                           (mismatch?'Day bias misaligned. ':'')+
                           (!sl&&!tgt?'High-risk behavioral pattern.':'Review entry triggers.') },
                  ].map(p => (
                    <div key={p.label} style={{ flex:1 }}>
                      <div style={{ color:p.color, fontSize:10, fontWeight:800, letterSpacing:1.2, textTransform:'uppercase', marginBottom:7 }}>{p.label}</div>
                      <div style={{ color:C.t1, fontSize:12, fontWeight:600, lineHeight:1.6 }}>{p.text}</div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ width:38, height:38, borderRadius:'50%', flexShrink:0,
                background:`${C.purple}12`, border:`1px solid ${C.purple}20`,
                display:'flex', alignItems:'center', justifyContent:'center', alignSelf:'center' }}>
                <Sparkles size={16} style={{ color:C.purple }} />
              </div>
            </div>
            {tradeReason && (
              <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.b1}`,
                display:'flex', alignItems:'flex-start', gap:8 }}>
                <span style={{ color:C.t3, fontSize:9, fontWeight:800, letterSpacing:1, textTransform:'uppercase', flexShrink:0, marginTop:2 }}>Your Reason</span>
                <span style={{ color:C.t1, fontSize:11, fontWeight:600, fontStyle:'italic', lineHeight:1.5,
                  overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                  "{tradeReason}"
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ══ DISCIPLINE STRIP ══ */}
        {dScore != null && (
          <div style={{ flexShrink:0, ...card({ padding:'8px 16px' }) }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              {/* Score donut */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3, flexShrink:0 }}>
                <DonutRing score={dScore} max={100} size={44} strokeWidth={4} color={dScore>=60?C.green:dScore>=40?C.amber:C.red} />
                <span style={{ color:C.t3, fontSize:8, fontWeight:700, letterSpacing:0.8 }}>DISCIPLINE</span>
              </div>
              <VR h={36} />
              {/* 4 checks inline */}
              <div style={{ flex:1, display:'flex', gap:14, flexWrap:'wrap' }}>
                {[
                  { label:'Stop Loss',  ok: disc.sl_placed },
                  { label:'Tagged',     ok: disc.tagged    },
                  { label:'Journaled',  ok: disc.journaled },
                  { label:'R:R ≥ 1.5',  ok: rr != null && rr >= 1.5 },
                ].map(({ label, ok }) => (
                  <div key={label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                    <div style={{ width:14, height:14, borderRadius:'50%', flexShrink:0,
                      background: ok ? `${C.green}20` : `${C.red}15`,
                      border: `1px solid ${ok ? C.green : C.red}40`,
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <span style={{ fontSize:8, color: ok ? C.green : C.red, fontWeight:900, lineHeight:1 }}>{ok ? '✓' : '✗'}</span>
                    </div>
                    <span style={{ color: ok ? C.t0 : C.t2, fontSize:10, fontWeight:700 }}>{label}</span>
                  </div>
                ))}
              </div>
              {/* violations count if any */}
              {disc.violations?.length > 0 && (
                <>
                  <VR h={36} />
                  <div style={{ textAlign:'center', flexShrink:0 }}>
                    <span style={{ color:C.red, fontSize:18, fontWeight:900, lineHeight:1 }}>{disc.violations.length}</span>
                    <div style={{ color:C.t3, fontSize:8, fontWeight:700, letterSpacing:0.8, textTransform:'uppercase' }}>Violation{disc.violations.length > 1 ? 's' : ''}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ MARKET CUES STRIP ══ */}
        {(gapType || prevDay.length > 0 || fii.fii_net != null || sentiment?.label) && (
          <div style={{ flexShrink:0, ...card({ padding:'8px 16px' }) }}>
            <div style={{ display:'flex', alignItems:'stretch', gap:0 }}>

              {/* Gap type */}
              {gapType && (
                <>
                  <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:4, paddingRight:14 }}>
                    <span style={{ color:C.t2, fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:'uppercase' }}>Gap / Open</span>
                    <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                      <span style={{ color:gapColor, fontSize:15, fontWeight:900, textTransform:'uppercase' }}>{gapLabel}</span>
                      {gapValue != null && <span style={{ color:gapColor, fontSize:12, fontWeight:800 }}>{gapValue >= 0 ? '+' : ''}{gapValue.toFixed(2)}%</span>}
                    </div>
                    {(bullSig != null || bearSig != null) && (
                      <div style={{ display:'flex', gap:8, marginTop:2 }}>
                        {bullSig != null && <span style={{ color:C.green, fontSize:10, fontWeight:700 }}>▲ {bullSig} Bull</span>}
                        {bearSig != null && <span style={{ color:C.red,   fontSize:10, fontWeight:700 }}>▼ {bearSig} Bear</span>}
                      </div>
                    )}
                  </div>
                  <VR h={48} />
                </>
              )}

              {/* Global indices — prev day US/Europe */}
              {prevDay.length > 0 && (
                <>
                  <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:4, paddingLeft:14, paddingRight:14 }}>
                    <span style={{ color:C.t2, fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:'uppercase' }}>Global Cues</span>
                    <div style={{ display:'flex', gap:10 }}>
                      {prevDay.slice(0, 3).map(idx => {
                        const chg = parseFloat(idx.change_pct || 0);
                        const c   = chg > 0 ? C.green : chg < 0 ? C.red : C.t2;
                        return (
                          <div key={idx.symbol} style={{ display:'flex', flexDirection:'column', gap:1 }}>
                            <span style={{ color:C.t2, fontSize:9, fontWeight:700 }}>{idx.name?.split(' ')[0] || idx.symbol}</span>
                            <span style={{ color:c, fontSize:12, fontWeight:900 }}>{chg >= 0 ? '+' : ''}{chg.toFixed(2)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <VR h={48} />
                </>
              )}

              {/* FII / DII */}
              {fii.fii_net != null && (
                <>
                  <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:4, paddingLeft:14, paddingRight:14 }}>
                    <span style={{ color:C.t2, fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:'uppercase' }}>FII / DII</span>
                    <div style={{ display:'flex', gap:12 }}>
                      {fii.fii_net != null && (
                        <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                          <span style={{ color:C.t2, fontSize:9, fontWeight:700 }}>FII</span>
                          <span style={{ color: fii.fii_net >= 0 ? C.green : C.red, fontSize:12, fontWeight:900 }}>
                            {fii.fii_net >= 0 ? '+' : ''}{(Math.abs(fii.fii_net) / 1e7).toFixed(0)} Cr
                          </span>
                        </div>
                      )}
                      {fii.dii_net != null && (
                        <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                          <span style={{ color:C.t2, fontSize:9, fontWeight:700 }}>DII</span>
                          <span style={{ color: fii.dii_net >= 0 ? C.green : C.red, fontSize:12, fontWeight:900 }}>
                            {fii.dii_net >= 0 ? '+' : ''}{(Math.abs(fii.dii_net) / 1e7).toFixed(0)} Cr
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <VR h={48} />
                </>
              )}

              {/* Sector leader / laggard */}
              {(sectorLead || sectorLag) && (
                <>
                  <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:4, paddingLeft:14, paddingRight:14 }}>
                    <span style={{ color:C.t2, fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:'uppercase' }}>Sectors</span>
                    <div style={{ display:'flex', gap:10 }}>
                      {sectorLead && (
                        <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                          <span style={{ color:C.green, fontSize:9, fontWeight:700 }}>▲ Leading</span>
                          <span style={{ color:C.t0, fontSize:11, fontWeight:800 }}>{sectorLead}</span>
                        </div>
                      )}
                      {sectorLag && (
                        <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                          <span style={{ color:C.red, fontSize:9, fontWeight:700 }}>▼ Lagging</span>
                          <span style={{ color:C.t0, fontSize:11, fontWeight:800 }}>{sectorLag}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <VR h={48} />
                </>
              )}

              {/* Sentiment */}
              {sentiment?.label && (() => {
                const sc = sentiment.label.includes('RISK_ON') ? C.green : sentiment.label.includes('RISK_OFF') ? C.red : C.amber;
                return (
                  <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:4, paddingLeft:14 }}>
                    <span style={{ color:C.t2, fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:'uppercase' }}>Sentiment</span>
                    <span style={{ color:sc, fontSize:13, fontWeight:900 }}>{sentiment.label.replace(/_/g,' ')}</span>
                    {sentiment.score != null && <span style={{ color:C.t2, fontSize:10, fontWeight:700 }}>Score: {sentiment.score}</span>}
                  </div>
                );
              })()}

            </div>
          </div>
        )}

        {/* ══ FOOTER ══ */}
        <div style={{ flexShrink:0, marginTop:'auto', paddingTop:7,
          borderTop:`1px solid ${C.b1}` }}>
          <div style={{ color:C.t3, fontSize:8, lineHeight:1.55, textAlign:'center', marginBottom:7 }}>
            For analysis &amp; educational purposes only · Not financial advice · Real markets are unpredictable — always trade with safety
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <img src={LOGO_B64} alt="TradeReflex" style={{ height:28, width:'auto', objectFit:'contain', flexShrink:0 }} />
            <div style={{ display:'flex', alignItems:'center', gap:6,
              background:`${C.purple}0C`, border:`1px solid ${C.purple}20`,
              borderRadius:99, padding:'3px 12px' }}>
              <Sparkles size={9} style={{ color:C.purple }} />
              <span style={{ color:C.purple, fontSize:9, fontWeight:700 }}>TradeReflex AI Coach</span>
            </div>
            <div style={{ color:C.t3, fontSize:9, fontWeight:600, letterSpacing:0.3 }}>
              Analyze · Reflect · Improve · Repeat
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
